import { type QueryKey, type UseQueryOptions, useQuery } from "@tanstack/react-query"
import { useMemo } from "react"

import type { GetCollectionData } from "@/utils/infiniteQueryDataUtils"

export const useAppQuery = <
  TParams extends Record<
    string,
    string | number | symbol | string[] | number[] | symbol[] | boolean | boolean[]
  >,
  TData,
  TError = unknown,
  TQueryKey extends QueryKey = QueryKey,
>(
  getData: (params: TParams) => Promise<GetCollectionData<TData>>,
  getQueryKey: (params: TParams) => TQueryKey,
  params: TParams = {} as TParams,
  options?: Omit<
    UseQueryOptions<GetCollectionData<TData>, TError, GetCollectionData<TData>, TQueryKey>,
    "queryKey"
  >
) => {
  const result = useQuery<GetCollectionData<TData>, TError, GetCollectionData<TData>, TQueryKey>({
    queryKey: getQueryKey(params),
    queryFn: () => {
      return getData({ ...params })
    },
    ...options,
  })

  const totalCount = useMemo(() => result.data?.["hydra:totalItems"] as number, [result.data])

  const flatData = useMemo(() => result.data?.["hydra:member"], [result.data])

  return { ...result, flatData, totalCount }
}
